import { z } from 'zod';
import { lenientString } from '../schema-extensions';

// ============================================================================
// CONSTANTS
// ============================================================================
const CONTACT_CONSTANTS = {
  PHONE_MAX_LENGTH: 20,
  WEB_URL_MAX_LENGTH: 500,
  EMAIL_MAX_LENGTH: 255,
  USERNAME_MAX_LENGTH: 100,
} as const;

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientContactNodeSchema = z.object({
  policyPhoneNumber: lenientString(CONTACT_CONSTANTS.PHONE_MAX_LENGTH, ''),
  webUrl: lenientString(CONTACT_CONSTANTS.WEB_URL_MAX_LENGTH, ''),
  email: lenientString(CONTACT_CONSTANTS.EMAIL_MAX_LENGTH, ''),
  username: lenientString(CONTACT_CONSTANTS.USERNAME_MAX_LENGTH, ''),
}).transform(val => ({
  policyPhoneNumber: val.policyPhoneNumber,
  webUrl: val.webUrl,
  email: val.email,
  username: val.username,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictContactNodeSchema = z.object({
  policyPhoneNumber: z.string()
    .max(CONTACT_CONSTANTS.PHONE_MAX_LENGTH, `Phone number must be ${CONTACT_CONSTANTS.PHONE_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
  webUrl: z.union([
    z.literal(''),
    z.string()
      .max(CONTACT_CONSTANTS.WEB_URL_MAX_LENGTH, `Web URL must be ${CONTACT_CONSTANTS.WEB_URL_MAX_LENGTH} characters or less`)
      .url('Must be a valid URL'),
  ]).optional().transform(val => val ?? ''),
  email: z.union([
    z.literal(''),
    z.string()
      .max(CONTACT_CONSTANTS.EMAIL_MAX_LENGTH, `Email must be ${CONTACT_CONSTANTS.EMAIL_MAX_LENGTH} characters or less`)
      .email('Must be a valid email'),
  ]).optional().transform(val => val ?? ''),
  username: z.string()
    .max(CONTACT_CONSTANTS.USERNAME_MAX_LENGTH, `Username must be ${CONTACT_CONSTANTS.USERNAME_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
}).transform(val => ({
  policyPhoneNumber: val.policyPhoneNumber,
  webUrl: val.webUrl,
  email: val.email,
  username: val.username,
}));

// ============================================================================
// TYPE
// ============================================================================
export type ContactNode = z.infer<typeof lenientContactNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace ContactNode {
  export const Constants = CONTACT_CONSTANTS;

  export function deserialize(data: unknown): ContactNode {
    return lenientContactNodeSchema.parse(data);
  }

  export function create(data: unknown): ContactNode {
    return strictContactNodeSchema.parse(data);
  }
}
